import api from '@js/actions/api';

import I18n from '@js/modules/translations';
import AnalyticsService from '@js/modules/analyticsService';

import {basketStore} from '@js/stores/basketStore';

import {trackLimitReached} from '@js/actions/trackingActions';

import Notification from '@js/components/layouts/notification';
import {paywallTier, Products} from '@js/components/modules/constants';
import {userStore} from '@js/stores/userStore';
import {productPath} from '@js/constants/routesHelper';


export const addToBasket = (productId, origin) => (
    api.post('/api/v1/baskets', {
        basket: {
            products: [{
                productId
            }],
            origin
        }
    })
);

export const fetchProduct = (productId, options = {}, requestOptions = {}) => (
    api.get(`/api/v1/products/${productId}`, {
        ...options
    }, requestOptions)
);

export const chargeBasket = (basketId, paymentData, options = {}) => (
    api.post('/api/v1/orders', {
        basketId: basketId,
        order: paymentData,
        ...options
    })
);

// Cap the modal PER functionality (not globally): a user who exhausted the upsell for one
// premium feature should still get it when they hit a *different* premium feature.
const PRODUCT_MODAL_SESSION_KEY = 'product_modal_shown_counts';
const PRODUCT_MODAL_MAX_SHOWS = 2;

const SOFT_NUDGE_SESSION_KEY = 'product_soft_nudge_shown_count';
const SOFT_NUDGE_MAX_SHOWS = 2;

const _countFor = (storageKey, functionality) => {
    try {
        const counts = JSON.parse(window.sessionStorage.getItem(storageKey) || '{}');
        return counts[functionality] || 0;
    } catch {
        return 0;
    }
};

const _incrementCountFor = (storageKey, functionality) => {
    try {
        const counts = JSON.parse(window.sessionStorage.getItem(storageKey) || '{}');
        counts[functionality] = (counts[functionality] || 0) + 1;
        window.sessionStorage.setItem(storageKey, JSON.stringify(counts));
    } catch {
        // sessionStorage unavailable (private mode) — ignore.
    }
};

const _formatDistanceLimit = () => {
    const value = Math.round((userStore.featureFlags.map_max_distance || 0) / 1000);
    const unitKey = userStore.unit === 'mi' ? 'mi' : 'km';

    return `${value}${I18n.t(`js.metrics.${unitKey}.distance_unit_k`)}`;
};

const _limitMessage = (requiredFunctionality) => {
    const params = { defaultValue: I18n.t('js.product.modal.soft_nudge') };

    if (requiredFunctionality === 'map_max_distance') {
        params.distance = _formatDistanceLimit();
    }

    return I18n.t(`js.product.features.${requiredFunctionality}.limit`, params);
};

const _showSoftNudge = (requiredFunctionality) => {
    if (_countFor(SOFT_NUDGE_SESSION_KEY, requiredFunctionality) >= SOFT_NUDGE_MAX_SHOWS) {
        return;
    }
    _incrementCountFor(SOFT_NUDGE_SESSION_KEY, requiredFunctionality);

    AnalyticsService.trackPaywallSoftNudge(requiredFunctionality);
    Notification.info(_limitMessage(requiredFunctionality));
};

let _lastLimitToastAt = 0;
const _showLimitToast = (requiredFunctionality) => {
    const now = Date.now();
    if (now - _lastLimitToastAt < 4_000) {
        return;
    }
    _lastLimitToastAt = now;

    const cta = `<a href="${productPath(Products.EXPLORER)}" data-quick-subscribe="${Products.EXPLORER}" data-quick-subscribe-origin="${requiredFunctionality}" class="ml-2 font-semibold underline whitespace-nowrap">${I18n.t('js.product.modal.btn')}</a>`;

    Notification.info(`${_limitMessage(requiredFunctionality)} ${cta}`);
};

export const showProductModal = (requiredFunctionality) => {
    // Intent-tiered: a low-intent functionality gets a non-blocking nudge, not the modal.
    if (paywallTier(requiredFunctionality) === 'soft') {
        _showSoftNudge(requiredFunctionality);
        return;
    }

    if (_countFor(PRODUCT_MODAL_SESSION_KEY, requiredFunctionality) >= PRODUCT_MODAL_MAX_SHOWS) {
        AnalyticsService.trackProductModalSuppressed(requiredFunctionality);
        _showLimitToast(requiredFunctionality);
        return;
    }

    _incrementCountFor(PRODUCT_MODAL_SESSION_KEY, requiredFunctionality);

    AnalyticsService.trackProductModalShow(requiredFunctionality);

    basketStore.productRequiredFunctionality = requiredFunctionality;

    basketStore.offerAvailable = false;
    basketStore.usage = null;

    trackLimitReached(requiredFunctionality)
        .then((response) => {
            basketStore.offerAvailable = Boolean(response && response.offer_available);
            basketStore.usage = response && response.usage ? response.usage : null;
        })
        .catch(() => {
            basketStore.offerAvailable = false;
            basketStore.usage = null;
        });

    if (basketStore.productModal && typeof basketStore.productModal.show === 'function') {
        basketStore.productModal.show();
    }
};

export const hideProductModal = () => {
    basketStore.productRequiredFunctionality = undefined;
    if (basketStore.productModal && typeof basketStore.productModal.hide === 'function') {
        basketStore.productModal.hide();
    }
};
